import { LogoutOutlined, MoonOutlined, SunOutlined, ThunderboltFilled } from "@ant-design/icons";
import { Avatar, Badge, Button, Layout, Menu, Space, Tag, Tooltip, Typography } from "antd";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router";

import { ROUTES, SECTIONS, sectionSurface } from "@/shared/config";

const { Header, Sider, Content } = Layout;

export interface ShellProps {
  children: ReactNode;
  login: string;
  role: string;
  isSuperadmin: boolean;
  /** Счётчики очереди — по ним оператор понимает, есть ли работа, не заходя в раздел. */
  pending: { deposits: number; withdrawals: number; comments: number };
  themeMode: "light" | "dark";
  onToggleTheme: () => void;
  onLogout: () => void;
}

/**
 * Иконка пункта меню в цвете своего раздела.
 *
 * На выбранном пункте цвет принудительно белый: подложка активного пункта —
 * это `colorPrimary`, и синяя иконка «Сводки» на синем фоне пропадала целиком.
 */
function NavIcon({
  section,
  selected,
}: {
  section: keyof typeof SECTIONS;
  selected: boolean;
}) {
  const { color, Icon } = SECTIONS[section];
  return <Icon aria-hidden style={{ color: selected ? "#fff" : color, fontSize: 16 }} />;
}

/**
 * Каркас админки: меню слева, шапка сверху, контент справа.
 *
 * Пункты, недоступные оператору, не показываются вовсе, а не блокируются:
 * серая кнопка «Настройки», которая всегда отвечает «нет прав», раздражает и
 * ничего не сообщает. Сервер при этом всё равно проверяет роль — меню лишь
 * убирает из виду то, что и так не сработает.
 *
 * Шапка липкая: таблицы заявок длинные, и кнопка выхода вместе с
 * переключателем темы не должны уезжать за верхний край на середине списка.
 */
export function AppShell({
  children,
  login,
  role,
  isSuperadmin,
  pending,
  themeMode,
  onToggleTheme,
  onLogout,
}: ShellProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  // Подсветка по самому длинному совпавшему префиксу: на `/users/41` активен
  // «Пользователи», а не «Сводка» (её путь `/` — префикс чего угодно).
  const active =
    [
      ROUTES.dashboard,
      ROUTES.deposits,
      ROUTES.withdrawals,
      ROUTES.users,
      ROUTES.comments,
      ROUTES.audit,
      ROUTES.robots,
      ROUTES.accruals,
      ROUTES.settings,
      ROUTES.admins,
    ]
      .filter((key) => pathname === key || (key !== "/" && pathname.startsWith(key)))
      .sort((a, b) => b.length - a.length)[0] ?? null;

  const items = [
    {
      key: ROUTES.dashboard,
      icon: <NavIcon section="dashboard" selected={active === ROUTES.dashboard} />,
      label: <Link to={ROUTES.dashboard}>{t("nav.dashboard")}</Link>,
    },
    {
      key: ROUTES.deposits,
      icon: <NavIcon section="deposits" selected={active === ROUTES.deposits} />,
      label: (
        <Link to={ROUTES.deposits}>
          <Space size={8}>
            {t("nav.deposits")}
            <Badge count={pending.deposits} size="small" />
          </Space>
        </Link>
      ),
    },
    {
      key: ROUTES.withdrawals,
      icon: <NavIcon section="withdrawals" selected={active === ROUTES.withdrawals} />,
      label: (
        <Link to={ROUTES.withdrawals}>
          <Space size={8}>
            {t("nav.withdrawals")}
            <Badge count={pending.withdrawals} size="small" />
          </Space>
        </Link>
      ),
    },
    {
      key: ROUTES.users,
      icon: <NavIcon section="users" selected={active === ROUTES.users} />,
      label: <Link to={ROUTES.users}>{t("nav.users")}</Link>,
    },
    {
      key: ROUTES.comments,
      icon: <NavIcon section="comments" selected={active === ROUTES.comments} />,
      label: (
        <Link to={ROUTES.comments}>
          <Space size={8}>
            {t("nav.comments")}
            {/* Счётчик считает только то, что ждёт решения: жалобы и карантин
                спама. Весь поток отзывов сюда не попадает — иначе бейдж
                перестал бы означать «нужна реакция». */}
            <Badge count={pending.comments} size="small" />
          </Space>
        </Link>
      ),
    },
    {
      key: ROUTES.audit,
      icon: <NavIcon section="audit" selected={active === ROUTES.audit} />,
      label: <Link to={ROUTES.audit}>{t("nav.audit")}</Link>,
    },
    ...(isSuperadmin
      ? [
          {
            key: ROUTES.robots,
            icon: <NavIcon section="robots" selected={active === ROUTES.robots} />,
            label: <Link to={ROUTES.robots}>{t("nav.robots")}</Link>,
          },
          {
            key: ROUTES.accruals,
            icon: <NavIcon section="accruals" selected={active === ROUTES.accruals} />,
            label: <Link to={ROUTES.accruals}>{t("nav.accruals")}</Link>,
          },
          {
            key: ROUTES.settings,
            icon: <NavIcon section="settings" selected={active === ROUTES.settings} />,
            label: <Link to={ROUTES.settings}>{t("nav.settings")}</Link>,
          },
          {
            key: ROUTES.admins,
            icon: <NavIcon section="admins" selected={active === ROUTES.admins} />,
            label: <Link to={ROUTES.admins}>{t("nav.admins")}</Link>,
          },
        ]
      : []),
  ];

  const roleColor = isSuperadmin ? SECTIONS.admins.color : SECTIONS.audit.color;

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sider breakpoint="lg" collapsedWidth={56} width={208}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "14px 14px 16px",
            overflow: "hidden",
          }}
        >
          <span
            aria-hidden
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flex: "none",
              width: 30,
              height: 30,
              borderRadius: 9,
              background: "linear-gradient(135deg, #1677ff 0%, #06b6d4 100%)",
              color: "#fff",
              fontSize: 15,
            }}
          >
            <ThunderboltFilled aria-hidden />
          </span>
          <div style={{ minWidth: 0 }}>
            <Typography.Text
              strong
              style={{ color: "#fff", fontSize: 14, display: "block", lineHeight: 1.25 }}
            >
              {t("app.title")}
            </Typography.Text>
            <Typography.Text
              style={{ color: "rgba(255,255,255,0.5)", fontSize: 11, whiteSpace: "nowrap" }}
            >
              {t("app.subtitle")}
            </Typography.Text>
          </div>
        </div>
        <Menu theme="dark" mode="inline" selectedKeys={active === null ? [] : [active]} items={items} />
      </Sider>

      <Layout>
        <Header
          style={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 8,
            // Полупрозрачный фон с размытием: под шапкой проезжают строки
            // таблицы, и сплошная заливка выглядит отрезанным куском.
            background: "color-mix(in srgb, var(--ant-color-bg-container) 82%, transparent)",
            backdropFilter: "blur(10px)",
            borderBottom: "1px solid var(--ant-color-split)",
          }}
        >
          {/* В одну строку: шапка ниже двух строк текста, и вертикальный блок
              вылезал бы за её границы. */}
          <Space size={8} align="center">
            <Avatar
              size={28}
              style={{ background: sectionSurface(roleColor), color: roleColor, fontWeight: 600 }}
            >
              {login.charAt(0).toUpperCase()}
            </Avatar>
            <Typography.Text strong>{login}</Typography.Text>
          </Space>
          <Tag
            color={isSuperadmin ? "purple" : "default"}
            style={{ marginInlineEnd: 0, paddingBlock: 1, paddingInline: 8 }}
          >
            {t(`app.role.${role}`, role)}
          </Tag>
          <Tooltip title={themeMode === "dark" ? "Светлая тема" : "Тёмная тема"}>
            <Button
              shape="circle"
              icon={themeMode === "dark" ? <SunOutlined aria-hidden /> : <MoonOutlined aria-hidden />}
              onClick={onToggleTheme}
              aria-label="Сменить тему"
            />
          </Tooltip>
          <Button icon={<LogoutOutlined aria-hidden />} onClick={onLogout}>
            {t("app.logout")}
          </Button>
        </Header>

        {/* `margin: 0 auto` к `maxWidth` обязателен: без него на мониторе шире
            предела контент прижат влево, а справа остаётся голая серая полоса
            — её и принимали за «непонятную рамку».

            Предел 1920, а не 1440: админка табличная, и на рабочей станции
            1920 колонки заявок должны помещаться целиком. Ограничение нужно
            только ультрашироким мониторам, где строка иначе растянулась бы на
            три тысячи пикселей. */}
        <Content style={{ padding: 16, maxWidth: 1920, width: "100%", margin: "0 auto" }}>
          {children}
        </Content>
      </Layout>
    </Layout>
  );
}
